// ค่าเฉพาะองค์กร: ค่าเริ่มต้นอยู่ในโค้ดนี้ (= หน้าตาปัจจุบัน ไม่มีชื่อ/โลโก้บริษัท) ส่วน org/theme.json
// และ org/config.json ที่ backend เสิร์ฟที่ /org/* ใช้ทับเฉพาะค่าที่ระบุ — ไฟล์ไม่มี/อ่านไม่ได้/ค่าผิด
// รูปแบบ จะใช้ค่าเริ่มต้นแทนเงียบๆ (ไม่ให้หน้าเว็บเปิดไม่ขึ้นเพราะ config เสีย)

export const SHADES = ['50', '100', '200', '300', '400', '500', '600', '700', '800', '900'];

export const DEFAULT_THEME = {
  primary: {
    50: '#eef2ff',
    100: '#e0e7ff',
    200: '#c7d2fe',
    300: '#a5b4fc',
    400: '#818cf8',
    500: '#4f46e5',
    600: '#4338ca',
    700: '#3730a3',
    800: '#312e81',
    900: '#1e1b4b',
  },
  secondary: {
    50: '#fff7ed',
    100: '#ffedd5',
    200: '#fed7aa',
    300: '#fdba74',
    400: '#fb923c',
    500: '#f97316',
    600: '#ea580c',
    700: '#c2410c',
    800: '#9a3412',
    900: '#7c2d12',
  },
};

export const ORG_LOGO_URL = '/org/logo.png';
export const ORG_ICON_URL = '/org/icon.png';
export const ORG_APP_ICON_URL = '/org/app-icon.png';

const DEFAULT_ORG = {
  // title: ข้อความบนแท็บเบราว์เซอร์ — null = ใช้ <title> ใน index.html ตามเดิม
  // name: ชื่อระบบที่แสดงในหน้าเว็บ (หน้าแรก, footer) — null = ข้อความเดิมของแต่ละหน้า
  brand: { title: null, name: null },
  // name: ชื่อบริษัทที่แสดงในหน้าเว็บ — null = ไม่แสดง (ไม่มีชื่อบริษัทเป็นค่าเริ่มต้น)
  company: { name: null },
  // logoUrl: มีไฟล์ org/logo.png จริงเมื่อไหร่ถึงเป็น URL — null = ไม่แสดงโลโก้
  logoUrl: null,
  // iconUrl: มีไฟล์ org/icon.png จริงเมื่อไหร่ถึงเป็น URL — ใช้เป็น favicon/ไอคอนหน้าจอหลัก; null = ไอคอนเดิม
  iconUrl: null,
  // appIconUrl: มีไฟล์ org/app-icon.png จริงเมื่อไหร่ถึงเป็น URL — ไอคอนติดตั้งแอปบนหน้าจอหลัก (iPhone) แยกจาก favicon; null = ใช้ iconUrl
  appIconUrl: null,
  theme: DEFAULT_THEME,
};

const HEX_COLOR = /^#[0-9a-f]{6}$/i;
const FETCH_TIMEOUT_MS = 3000;

let currentOrg = DEFAULT_ORG;

export const hexToRgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));

async function fetchJson(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const res = await fetch(url, { cache: 'no-cache', signal: controller.signal });
    const isJson = (res.headers.get('content-type') || '').includes('json');

    return res.ok && isJson ? await res.json() : null;
  } catch (error) {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

// รับเฉพาะสี hex 6 หลักที่ถูกต้องเป็นรายเฉด เฉดไหนไม่ระบุ/ผิดรูปแบบ ใช้ค่าเริ่มต้นของเฉดนั้น
function mergeScale(defaults, custom) {
  const merged = {};

  SHADES.forEach((shade) => {
    merged[shade] = custom && HEX_COLOR.test(custom[shade]) ? custom[shade] : defaults[shade];
  });

  return merged;
}

const cleanText = (value) => (typeof value === 'string' ? value.trim() : '');

function mergeOrg(config, themeFile, hasLogo, hasIcon, hasAppIcon) {
  const title = cleanText(config?.brand?.title);
  const brandName = cleanText(config?.brand?.name);
  const companyName = cleanText(config?.company?.name);

  return {
    brand: { title: title || DEFAULT_ORG.brand.title, name: brandName || DEFAULT_ORG.brand.name },
    company: { name: companyName || DEFAULT_ORG.company.name },
    logoUrl: hasLogo ? ORG_LOGO_URL : DEFAULT_ORG.logoUrl,
    iconUrl: hasIcon ? ORG_ICON_URL : DEFAULT_ORG.iconUrl,
    appIconUrl: hasAppIcon ? ORG_APP_ICON_URL : DEFAULT_ORG.appIconUrl,
    theme: {
      primary: mergeScale(DEFAULT_THEME.primary, themeFile?.primary),
      secondary: mergeScale(DEFAULT_THEME.secondary, themeFile?.secondary),
    },
  };
}

// ตั้ง CSS variable ที่ Tailwind (primary-*/secondary-*/theme-*) อ้างอิง — เก็บเป็น "r g b"
// เพื่อให้ใช้ร่วมกับ opacity utility ของ Tailwind ได้
// เฉดกลางระหว่าง primary-400 กับ primary-500 — ใช้แทน #6366f1 (indigo-500 ของ Tailwind) ที่หลายหน้าใช้เป็นจุดเริ่มของสีไล่เฉด
// ถ้าเป็นสีเริ่มต้นของระบบจะได้ #6366f1 เป๊ะ (หน้าตาเดิมไม่เปลี่ยน) ถ้า org ตั้งสีเอง จะเป็นค่ากึ่งกลางของสองเฉดนั้น
const DEFAULT_MID_HEX = '#6366f1';

function midShadeHex(primary) {
  const isDefault = SHADES.every((shade) => primary[shade].toLowerCase() === DEFAULT_THEME.primary[shade]);
  if (isDefault) {
    return DEFAULT_MID_HEX;
  }
  const a = hexToRgb(primary['400']);
  const b = hexToRgb(primary['500']);

  return `#${a.map((v, i) => Math.round((v + b[i]) / 2).toString(16).padStart(2, '0')).join('')}`;
}

function applyCssVars(theme) {
  const root = document.documentElement;

  root.style.setProperty('--color-primary-450', hexToRgb(midShadeHex(theme.primary)).join(' '));

  ['primary', 'secondary'].forEach((name) => {
    SHADES.forEach((shade) => {
      root.style.setProperty(`--color-${name}-${shade}`, hexToRgb(theme[name][shade]).join(' '));
    });
  });
}

// เช็คว่ามีไฟล์รูปจริงไหมก่อน render (HEAD ไม่โหลดตัวรูป) ไม่งั้นต้องรอ img onerror แล้วหน้าจอกระพริบ
async function imageExists(url) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const res = await fetch(url, { method: 'HEAD', cache: 'no-cache', signal: controller.signal });

    return res.ok && (res.headers.get('content-type') || '').startsWith('image/');
  } catch (error) {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

export async function loadOrg() {
  const [config, themeFile, hasLogo, hasIcon, hasAppIcon] = await Promise.all([
    fetchJson('/org/config.json'),
    fetchJson('/org/theme.json'),
    imageExists(ORG_LOGO_URL),
    imageExists(ORG_ICON_URL),
    imageExists(ORG_APP_ICON_URL),
  ]);

  currentOrg = mergeOrg(config, themeFile, hasLogo, hasIcon, hasAppIcon);
  applyCssVars(currentOrg.theme);

  return currentOrg;
}

export const getOrg = () => currentOrg;

// สี hex ของเฉดในธีมปัจจุบัน — ใช้เฉพาะจุดที่ต้องการค่า hex จริง (เช่นส่งเข้า alpha() ของ MUI, ต่อ hex alpha, หรือสร้าง PDF)
// ต้องเรียกตอนใช้งาน (ในฟังก์ชัน/ตอน render) ไม่ใช่ตอนโหลดโมดูล เพราะธีมโหลดหลัง import; กรณีอื่นใช้ CSS variable
export const brandHex = (shade = 500, group = 'primary') => currentOrg.theme[group][shade];

// สีทึบ (hex) ที่ได้จากเฉดของสีหลักผสมกับสีขาวตามสัดส่วน ratio (0..1) — ใช้แทนสีโปร่งแสงในที่ที่ต้องเป็นสีทึบเสมอ
// (เช่นเซลล์ sticky ที่ห้ามเห็นเนื้อหาข้างใต้) ค่าเริ่มต้น brandTint(0.16) = #e3e1fb เท่าเดิม
export const brandTint = (ratio, shade = 500) => {
  const channels = hexToRgb(brandHex(shade)).map((v) => Math.round(v * ratio + 255 * (1 - ratio)));

  return `#${channels.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
};
